import React, { useCallback, useState } from "react";

import uniqueId from "lodash.uniqueid";

import {
  HvButton,
  HvInput,
  HvLabel,
  HvTypography,
  HvWarningText,
} from "@hitachivantara/uikit-react-core";

import { Distance, ValueComponentProps } from "../types";
import Context from "../Context";
import { useStyles } from "./styles";

interface DistanceValueProps extends ValueComponentProps {
  value?: Distance;
}

const DistanceValue = ({
  id,
  value = {},
  initialTouched = false,
}: DistanceValueProps): JSX.Element => {
  const classes = useStyles();

  const context = React.useContext(Context);
  const { labels, selectLocation, dispatchAction } = context;

  const onLocationChange = useCallback(
    (data) => {
      if (data) {
        dispatchAction({
          type: "set-value",
          id,
          value: { distance: value?.distance, ...data },
        });
      } else {
        dispatchAction({ type: "set-value", id, value: null });
      }
    },
    [dispatchAction, id, value?.distance]
  );

  const onDistanceChange = useCallback(
    (evt, data) => {
      const distance = data ?? null;
      dispatchAction({
        type: "set-value",
        id,
        value: {
          latitude: value?.latitude,
          longitude: value?.longitude,
          address: value?.address,
          distance,
        },
      });
    },
    [dispatchAction, id, value?.address, value?.latitude, value?.longitude]
  );

  const onSelectLocationButton = () => {
    selectLocation({
      onSelection: onLocationChange,
      location: value,
    });
  };

  const [touchedDistance, setTouchedDistance] = useState(initialTouched);
  const [touchedAddress, setTouchedAddress] = useState(initialTouched);

  const elementId = uniqueId("distance");

  const { distance = "" } = value;
  let distanceValidation;
  if (touchedDistance) {
    if (distance === "") {
      distanceValidation = "required";
    } else if (Number.isNaN(Number(distance)) || Number(distance) < 0) {
      distanceValidation = "invalid";
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.radius}>
        <HvLabel
          label={labels.rule.value.distance.label}
          htmlFor={`${elementId}-distance-input`}
          className={classes.label}
        />
        <div style={{ width: "min-content" }}>
          <div className={classes.inputContainer}>
            <HvInput
              className={classes.input}
              id={`${elementId}-distance`}
              name={`${elementId}-distance`}
              value={distance.toString()}
              onChange={onDistanceChange}
              onBlur={() => {
                setTouchedDistance(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              status={
                !touchedDistance
                  ? "standBy"
                  : distanceValidation != null
                  ? "invalid"
                  : "valid"
              }
              required
              inputProps={{
                autoComplete: "off",
              }}
              placeholder={labels.rule.value.distance.placeholder}
              aria-errormessage={`${elementId}-distance-error`}
            />
            <HvTypography noWrap>
              {labels.rule.value.distance.connectorText}
            </HvTypography>
          </div>
          <HvWarningText
            disableBorder
            id={`${elementId}-distance-error`}
            isVisible={distanceValidation != null}
          >
            {
              labels.rule.value.distance.validation[
                distanceValidation as "required" | "invalid"
              ]
            }
          </HvWarningText>
        </div>
      </div>

      <HvInput
        className={classes.location}
        id={`${elementId}-address`}
        name={`${elementId}-address`}
        required
        status={
          !touchedAddress
            ? "standBy"
            : (value?.address || "") === ""
            ? "invalid"
            : "valid"
        }
        statusMessage={labels.rule.value.distance.validation.required}
        onBlur={() => {
          setTouchedAddress(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        readOnly
        inputProps={{
          autoComplete: "off",
        }}
        aria-label={labels.rule.value.text.label}
        placeholder="—"
        value={value?.address || ""}
      />
      <HvButton
        className={classes.button}
        category="ghost"
        onClick={onSelectLocationButton}
      >
        <HvTypography noWrap>{labels.rule.value.distance.button}</HvTypography>
      </HvButton>
    </div>
  );
};

export default React.memo(DistanceValue);
